/**
 * EmojiPicker — Popover-Emoji-Auswahl für Icon-Freitextfelder.
 *
 * Warum eine eigene Komponente statt der Betriebssystem-Auswahl: das native
 * Emoji-Panel (Win+. / Ctrl+Cmd+Space) lässt sich aus JavaScript heraus nicht
 * öffnen — es gibt dafür keine Web-API. Ein npm-Picker (`unicode-emoji-picker`)
 * wäre die Alternative, zieht aber eine Web-Component samt eigener
 * Datenquelle und Theming ein; für die Handvoll Icons im Leadquellen-Katalog
 * ist eine kuratierte Liste passender, dark-mode-fähig und ohne Build-Kosten.
 *
 * Nutzung:
 *   <EmojiPickerField value={icon} onChange={setIcon} dark={dark} label="Icon" />
 *
 * Die Tastatureingabe bleibt erhalten — der Nutzer kann weiterhin ein Emoji
 * direkt ins Feld tippen oder über die OS-Auswahl einfügen.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, getThemeColors } from '../lib/theme';

/* ─── Emoji-Katalog ──────────────────────────────────────────────────────
 *
 * Kuratiert auf das, was als Quellen-/Kategorie-Icon im CRM sinnvoll ist.
 * `keywords` ist deutsch, damit die Suche zur UI-Sprache passt.
 */
export interface EmojiGroup {
  /** i18n-Key des Gruppen-Labels. */
  labelKey: string;
  emojis: { char: string; keywords: string }[];
}

export const EMOJI_GROUPS: EmojiGroup[] = [
  {
    labelKey: 'emoji_picker.group.marketing',
    emojis: [
      { char: '🌐', keywords: 'website web internet seite global' },
      { char: '🔍', keywords: 'suche google seo lupe' },
      { char: '📣', keywords: 'kampagne werbung megafon ankuendigung' },
      { char: '📢', keywords: 'kampagne werbung lautsprecher' },
      { char: '📈', keywords: 'wachstum statistik ads performance' },
      { char: '🎯', keywords: 'ziel targeting zielgruppe' },
      { char: '💡', keywords: 'idee empfehlung tipp' },
      { char: '🏷️', keywords: 'label tag kategorie' },
      { char: '⭐', keywords: 'stern bewertung favorit' },
      { char: '🔔', keywords: 'benachrichtigung glocke hinweis' },
      { char: '🎁', keywords: 'aktion gutschein geschenk' },
      { char: '🚀', keywords: 'launch start rakete' },
    ],
  },
  {
    labelKey: 'emoji_picker.group.kontakt',
    emojis: [
      { char: '📞', keywords: 'telefon anruf hotline' },
      { char: '☎️', keywords: 'telefon festnetz' },
      { char: '📱', keywords: 'handy mobil smartphone' },
      { char: '✉️', keywords: 'email mail brief nachricht' },
      { char: '📧', keywords: 'email mail newsletter' },
      { char: '💬', keywords: 'chat nachricht whatsapp' },
      { char: '📝', keywords: 'formular notiz kontaktformular' },
      { char: '📅', keywords: 'termin kalender datum' },
      { char: '🤝', keywords: 'empfehlung partner kooperation' },
      { char: '👥', keywords: 'kontakte personen gruppe' },
      { char: '🗣️', keywords: 'mundpropaganda empfehlung sprechen' },
      { char: '📬', keywords: 'post briefkasten eingang' },
    ],
  },
  {
    labelKey: 'emoji_picker.group.social',
    emojis: [
      { char: '📘', keywords: 'facebook blau social' },
      { char: '📷', keywords: 'instagram foto kamera' },
      { char: '💼', keywords: 'linkedin xing beruf business' },
      { char: '🎥', keywords: 'youtube video tiktok' },
      { char: '🐦', keywords: 'twitter x vogel' },
      { char: '📺', keywords: 'tv fernsehen werbung' },
      { char: '📻', keywords: 'radio funk' },
      { char: '📰', keywords: 'zeitung presse print' },
      { char: '🖨️', keywords: 'flyer print druck' },
      { char: '🎧', keywords: 'podcast audio' },
    ],
  },
  {
    labelKey: 'emoji_picker.group.pflege',
    emojis: [
      { char: '❤️', keywords: 'herz pflege liebe' },
      { char: '🏥', keywords: 'krankenhaus klinik' },
      { char: '🏠', keywords: 'zuhause haus wohnen' },
      { char: '🧑‍⚕️', keywords: 'arzt pflegekraft medizin' },
      { char: '💊', keywords: 'medikament apotheke' },
      { char: '🩺', keywords: 'arzt stethoskop diagnose' },
      { char: '♿', keywords: 'rollstuhl barrierefrei behinderung' },
      { char: '🧓', keywords: 'senior aeltere person' },
      { char: '👨‍👩‍👧', keywords: 'familie angehoerige' },
      { char: '🕊️', keywords: 'hospiz frieden taube' },
    ],
  },
  {
    labelKey: 'emoji_picker.group.sonstiges',
    emojis: [
      { char: '🔵', keywords: 'punkt blau kreis' },
      { char: '🟢', keywords: 'punkt gruen kreis' },
      { char: '🟡', keywords: 'punkt gelb kreis' },
      { char: '🔴', keywords: 'punkt rot kreis' },
      { char: '🟣', keywords: 'punkt lila kreis' },
      { char: '⚫', keywords: 'punkt schwarz kreis' },
      { char: '📌', keywords: 'pin merken wichtig' },
      { char: '🔗', keywords: 'link verweis kette' },
      { char: '🗂️', keywords: 'ordner ablage kategorie' },
      { char: '🧾', keywords: 'beleg rechnung quittung' },
      { char: '🏢', keywords: 'firma unternehmen buero' },
      { char: '🚗', keywords: 'auto fahrzeug aussendienst' },
      { char: '✅', keywords: 'haken erledigt ok' },
      { char: '❓', keywords: 'unbekannt frage sonstige' },
    ],
  },
];

/** Alle Emojis, deren Zeichen oder Stichworte zur Suche passen. */
export function filterEmojiGroups(query: string): EmojiGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return EMOJI_GROUPS;
  return EMOJI_GROUPS
    .map(g => ({
      ...g,
      emojis: g.emojis.filter(e => e.keywords.includes(q) || e.char === query.trim()),
    }))
    .filter(g => g.emojis.length > 0);
}

/* ─── Popover ────────────────────────────────────────────────────────────── */

interface EmojiPickerProps {
  onPick: (emoji: string) => void;
  onClose: () => void;
  dark: boolean;
  /** Erlaubt „kein Icon" (setzt ''). */
  onClear?: () => void;
}

export function EmojiPicker({ onPick, onClose, dark, onClear }: EmojiPickerProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  // Klick außerhalb / Escape schließt — sonst bliebe das Popover über dem
  // Formular stehen, sobald der Nutzer woanders weiterarbeitet.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const groups = filterEmojiGroups(query);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={tr('emoji_picker.title')}
      style={{
        position: 'absolute', zIndex: 60, top: 'calc(100% + 6px)', left: 0,
        width: 268, maxHeight: 300, overflowY: 'auto',
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
        boxShadow: '0 12px 28px rgba(16,24,40,0.18)', padding: 10,
      }}
    >
      {/* KEIN autoFocus: das Popover öffnet beim Fokussieren des Icon-Feldes —
          ein Autofokus hier würde den Fokus sofort abziehen und das direkte
          Eintippen eines Emojis unmöglich machen. Die Suche ist ein Klick
          entfernt. */}
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder={tr('emoji_picker.search_placeholder')}
        aria-label={tr('emoji_picker.search_placeholder')}
        style={{
          width: '100%', boxSizing: 'border-box', marginBottom: 8,
          border: `1px solid ${t.border}`, borderRadius: 8, padding: '6px 9px',
          fontSize: 12, background: t.inputBg, color: t.heading,
          fontFamily: 'Outfit, sans-serif', outline: 'none',
        }}
      />

      {groups.length === 0 && (
        <div style={{ fontSize: 12, color: t.muted, padding: '8px 2px' }}>
          {tr('emoji_picker.no_results')}
        </div>
      )}

      {groups.map(g => (
        <div key={g.labelKey} style={{ marginBottom: 8 }}>
          <div style={{
            fontSize: 10, fontWeight: 600, color: t.muted, marginBottom: 4,
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            {tr(g.labelKey)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {g.emojis.map(e => (
              <button
                key={e.char}
                type="button"
                title={e.char}
                aria-label={e.char}
                onClick={() => { onPick(e.char); onClose(); }}
                style={{
                  background: 'transparent', border: 'none', borderRadius: 6,
                  fontSize: 18, lineHeight: '28px', height: 30, cursor: 'pointer',
                  padding: 0,
                }}
                onMouseEnter={ev => { ev.currentTarget.style.background = dark ? '#1f2a37' : '#f2f4f7'; }}
                onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; }}
              >
                {e.char}
              </button>
            ))}
          </div>
        </div>
      ))}

      {onClear && (
        <button
          type="button"
          onClick={() => { onClear(); onClose(); }}
          style={{
            width: '100%', marginTop: 2, padding: '6px 0', borderRadius: 7,
            border: `1px solid ${t.border}`, background: 'transparent',
            color: t.muted, fontSize: 12, cursor: 'pointer',
            fontFamily: 'Outfit, sans-serif',
          }}
        >
          {tr('emoji_picker.clear')}
        </button>
      )}
    </div>
  );
}

/* ─── Feld + Picker ──────────────────────────────────────────────────────── */

interface EmojiPickerFieldProps {
  value: string;
  onChange: (value: string) => void;
  dark: boolean;
  /** Zugänglicher Name des Eingabefeldes. */
  label: string;
  placeholder?: string;
  inputStyle?: React.CSSProperties;
}

/**
 * Eingabefeld, das auf Klick die Emoji-Auswahl öffnet. Freitext bleibt
 * möglich (der Katalog ist eine Auswahlhilfe, keine Einschränkung — das
 * Backend nimmt jeden String als `icon` entgegen).
 */
export function EmojiPickerField({
  value, onChange, dark, label, placeholder, inputStyle,
}: EmojiPickerFieldProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const id = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Nach der Auswahl den Fokus zurück ins Feld holen: der geklickte
   * Emoji-Button wird beim Schließen ausgehängt, der Fokus landet sonst auf
   * <body> und die Tab-Reihenfolge beginnt von vorn.
   */
  const closeAndRefocus = () => {
    setOpen(false);
    inputRef.current?.focus({ preventScroll: true });
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        ref={inputRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          border: `1px solid ${open ? BRAND : t.border}`, borderRadius: 8,
          padding: '7px 10px', fontSize: 16, color: t.heading,
          background: t.inputBg, fontFamily: 'Outfit, sans-serif',
          outline: 'none', width: '100%', textAlign: 'center', cursor: 'pointer',
          ...inputStyle,
        }}
      />
      {open && (
        <EmojiPicker
          dark={dark}
          onPick={emoji => { onChange(emoji); closeAndRefocus(); }}
          onClear={() => { onChange(''); closeAndRefocus(); }}
          onClose={() => setOpen(false)}
        />
      )}
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {tr('emoji_picker.hint')}
      </span>
    </div>
  );
}
